import { useGetInventoryEnvironmentOptions } from "@/api/useInventory"
import { SEVERITIES_FILTER_OPTIONS } from "@/const/options.constants"
import { TAB_KEYS_BY_PAGE } from "@/const/tab.constants"
import { TEASERS } from "@/const/teaser.constants"
import { isDataAvailable } from "@/helpers/context.helper"
import { isSelfHosted } from "@/helpers/self-hosted.helper"
import { useInitialRender } from "@/hooks/useInitialRender"
import { useNumberConvertor } from "@/hooks/useNumberConvertor"
import { useOrderBy } from "@/hooks/useOrderBy"
import { useTooltip } from "@/hooks/useTooltip"
import { useTranslation } from "@/hooks/useTranslation"
import { useFeatureFlags } from "@/hooks/useUser"
import { ScanProgressEmptyTable } from "@/molecules/EmptyTable"
import { DatePickerExportContainer } from "@/organisms/DatePickerExportContainer"
import { IMAGE_DRAWER_ID } from "@/organisms/Drawers/const"
import { useDrawer } from "@/organisms/Drawers/hooks/useDrawer"
import { Filter } from "@/organisms/FilterNew/Filter"
import { InputSmartSearchLoading, SmartSearch } from "@/organisms/SmartSearch"
import { useImageInventory } from "@/pages/DiscoveryPage/application/image-inventory/useImageInventory"
import { showScanningProgressImage } from "@/pages/DiscoveryPage/utils/table.utils"
import { TabTemplate } from "@/templates/TabTemplate"
import { getColumnsTable } from "./getColumnsTable"
import { getFilters } from "./getItemsFilter"

import type { ImagesTabProps } from "."

export const ImagesTab: React.FC<ImagesTabProps> = (props) => {
  const selfHosted = isSelfHosted()
  const { useActiveKeyTabsInventory } = props
  const { onSorterChange, ...orderByProps } = useOrderBy()
  const { t } = useTranslation()

  const { environmentOptions, isLoading: isLoadingEnvironments } =
    useGetInventoryEnvironmentOptions()

  const filters = getFilters(t, environmentOptions)
  const convertors = useNumberConvertor()
  const { activeKey, config = {} } = useActiveKeyTabsInventory()
  const previewMode = !!config.headers
  const { idsInDrawers, onRowClick } = useDrawer(IMAGE_DRAWER_ID, {
    filters: ["severity", "riskId", "runtime"]
  })
  const columns = getColumnsTable({
    sorter: orderByProps,
    t,
    convertors,
    previewMode,
    onRowClick,
    selfHosted
  })
  const TAB_KEYS = TAB_KEYS_BY_PAGE.INVENTORY

  const context = useInitialRender(
    useImageInventory(filters, {
      enabled: activeKey === TAB_KEYS[2],
      ...config
    })
  )
  const { response, isLoading } = context
  const { data, metadata } = response || {}
  const featureFlags = useFeatureFlags()
  const dataReadinessFF = featureFlags.dataReadiness
  const filterDisabled = !!dataReadinessFF || isLoadingEnvironments
  const noData = !isDataAvailable(context)
  const disableExport = !isDataAvailable(context) || filterDisabled
  const placement = "images-page"
  const showScanningProgress = noData && showScanningProgressImage(featureFlags)

  useTooltip(data)

  return (
    <TabTemplate
      resizable
      dataSource={data}
      metadata={metadata}
      columns={columns}
      loading={isLoading}
      locale={{
        customEmptyComponent: showScanningProgress && (
          <ScanProgressEmptyTable isLoading={isLoading} />
        ),
        teasers: {
          dataReadiness: true,
          ciIntegrated: true,
          kortexInstalled: TEASERS.KORTEX_INSTALLED.noActiveSensorsDeployed(t)
        }
      }}
      SearchComponent={
        !previewMode && (
          <Filter.Provider keys={Object.keys(filters)}>
            <Filter.Template
              isLoading={filterDisabled}
              aside={
                <DatePickerExportContainer
                  isLoading={isLoading}
                  disableExport={disableExport}
                  exportType="Image"
                  placement={placement}
                />
              }
              search={
                filterDisabled ? (
                  <InputSmartSearchLoading />
                ) : (
                  <SmartSearch
                    id="smart-search-images-inventory"
                    filters={filters}
                    searchedVulnerabilities={metadata?.searchedVulnerabilities}
                  />
                )
              }
            >
              <Filter.Insights
                isLoading={filterDisabled}
                filters={filters}
                placement={placement}
              />
              <Filter.Checkbox
                isLoading={filterDisabled}
                order="none"
                keyFilter="severity"
                items={SEVERITIES_FILTER_OPTIONS}
                placeHolderDefaultValue="Severity"
                placement={placement}
              />
            </Filter.Template>
          </Filter.Provider>
        )
      }
      onChange={(_pagination, _filters, sorter) => {
        onSorterChange(sorter)
      }}
      idsInDrawers={idsInDrawers}
      disableDrawer={previewMode}
      onRowClick={onRowClick}
    />
  )
}
